/**
 * Какие колонки таблицы показывать, и память об этом между заходами.
 *
 * ДВАДЦАТЬ КОЛОНОК НУЖНЫ НЕ ВСЕМ И НЕ ВСЕГДА. Одному нужны показы
 * и клики, другому — только заказы и цена заказа. Заставлять и того,
 * и другого читать все двадцать значит заставлять их каждый раз искать
 * свои три.
 *
 * ВЫБОР ПОМНИТСЯ, иначе он бесполезен: настройка, которую надо повторять
 * при каждом заходе, хуже её отсутствия — человек привыкает не трогать её
 * вовсе. Память лежит в браузере: это не общая настройка кабинета,
 * а привычка конкретного человека за конкретным столом.
 *
 * ПОРТИТСЯ ПАМЯТЬ — ПОКАЗЫВАЕМ ВСЁ. Сохранённое значение может оказаться
 * мусором: другая версия, чужая вкладка, ручная правка. Скрыть по такому
 * значению половину таблицы значит спрятать данные без ведома человека;
 * показать лишнее — всего лишь шумно.
 */

const KEY = 'rk.clusters.hidden';

/**
 * Порядок граф — отдельная запись, а не часть набора.
 *
 * Их можно менять независимо: спрятать «Позицию», ничего не переставив,
 * или переставить, ничего не пряча. Одна общая запись означала бы, что
 * порча любой половины стирает обе.
 */
const KEY_ORDER = 'rk.clusters.order';

/**
 * Колонки, которые скрыть нельзя.
 *
 * Без запроса строка перестаёт быть строкой: остаются числа, о которых
 * неизвестно, к чему они относятся. Действия — единственный способ
 * что-то сделать со строкой, и таблица без них становится только
 * для чтения, о чём человек не просил.
 */
export const ALWAYS_ON = ['normQuery', 'actions'] as const;

/**
 * ═══════════════════════════════════════════════════════════════════════
 * ЧТО ПОКАЗАНО, ПОКА ЧЕЛОВЕК НЕ ВЫБРАЛ САМ — ПЕРЕДАЧА 02.09.2026.
 *
 * ТЗ, раздел 2: «Десять граф, из них две скрыты по умолчанию
 * („Позиция“, „Доля“)». То есть открыто всё, кроме этих двух.
 *
 * ═══════════════════════════════════════════════════════════════════════
 * ЗДЕСЬ БЫЛО СВЁРНУТО ДЕВЯТЬ ГРАФ, И ДОВОД БЫЛ ЧЕСТНЫЙ: шестнадцать
 * столбцов давали таблицу в 1800 точек при эталонных 1398, и первое,
 * что видел человек, — поперечную полосу прокрутки.
 *
 * ЧТО ИЗМЕНИЛОСЬ. Граф стало меньше: «Выручка» удалена целиком
 * (Wildberries её по запросу не отдаёт — причина сказана в подвале
 * один раз, а не прочерком в каждой строке), служебные «Первый показ»
 * и «Суток» свёрнуты по-прежнему, «Товар» показывается только там,
 * где товаров больше одного. Ширина проверена замером в браузере,
 * а не выведена на бумаге.
 *
 * ПОЧЕМУ СКРЫТЫ ИМЕННО «ПОЗИЦИЯ» И «ДОЛЯ». Обе отвечают на вопрос
 * «где мы среди прочих», а не «что делать со ставкой». Их открывают,
 * когда разбираются с конкретным запросом, — и тогда открывают руками.
 * ═══════════════════════════════════════════════════════════════════════
 */
export const СКРЫТЫ_СНАЧАЛА: readonly string[] = [
  'avgPos',
  'share',
  /* СЛУЖЕБНЫЕ — НЕ ПРО РЕКЛАМУ, А ПРО НАШИ ДАННЫЕ. «Первый показ»
     и «Суток» нужны при разборе минусации, а не при чтении списка;
     ТЗ их среди десяти граф не называет вовсе. */
  'firstSeenAt',
  'days',
  /* ТОВАР — ТОЛЬКО КОГДА ТОВАРОВ НЕСКОЛЬКО. Сама графа и заводится
     условно (`columns.tsx`), но скрытие оставлено: у кампании
     с одним товаром столбец повторял бы одно имя во всех строках. */
  'nmId',
];

export function canHide(id: string): boolean {
  return !(ALWAYS_ON as readonly string[]).includes(id);
}

/** Прочитать сохранённое. Всё, что не разобралось, — «скрытых нет». */
/**
 * ═══════════════════════════════════════════════════════════════════════
 * СТАВКА ОТКРЫТА ВЕЗДЕ — ПЕРЕДАЧА 02.09.2026.
 *
 * ЗДЕСЬ БЫЛА РАЗВИЛКА: на самостоятельном экране столбец «Ставка»
 * сворачивался, на вкладке кампании открывался. Довод был такой:
 * на экране запросов смотрят, ЧТО ищут, а ставки правят внутри
 * кампании.
 *
 * ТЗ ставит вопрос иначе: панель ОДНА, и графа «Ставка, ₽» входит
 * в первую же группу — «Запрос и ставка». Панель, которая в двух местах
 * показывает разный набор граф, — это две панели, чего ТЗ и запрещает
 * первым пунктом приёмки.
 *
 * Довод «на экране запросов ставка не нужна» держался на том, что
 * там нет одной кампании. Но ставка у нас ТОВАРНАЯ, а товар известен
 * в любой строке — значит и править её можно откуда угодно.
 *
 * Признак `вКампании` оставлен: он ещё нужен снаружи, и убирать его
 * из подписи ради одной строки — лишняя правка в чужих местах.
 * ═══════════════════════════════════════════════════════════════════════
 */
export function loadHidden(
  storage: Pick<Storage, 'getItem'>,
  вКампании = false,
): Set<string> {
  try {
    const raw = storage.getItem(KEY);
    /**
     * НИЧЕГО НЕ СОХРАНЕНО — ЭТО ПЕРВЫЙ ЗАХОД, а не «показать всё».
     * Разница видна: сохранённый ПУСТОЙ список («я открыл все двадцать
     * и хочу их видеть») остаётся пустым, а отсутствие записи даёт
     * набор эталона.
     */
    if (raw === null) {
      /* `вКампании` больше не меняет набор — довод выше. */
      void вКампании;
      return new Set(СКРЫТЫ_СНАЧАЛА);
    }
    if (!raw) return new Set();
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return new Set();
    return new Set(parsed.filter((v): v is string => typeof v === 'string').filter(canHide));
  } catch {
    return new Set();
  }
}

/**
 * Сохранить.
 *
 * Хранилище может отказать: приватный режим, переполнение, запрет.
 * Ронять из-за этого таблицу нельзя — человек потеряет не настройку,
 * а работу.
 */
export function saveHidden(storage: Pick<Storage, 'setItem'>, hidden: Set<string>): void {
  try {
    storage.setItem(KEY, JSON.stringify([...hidden].filter(canHide)));
  } catch {
    /* не сохранилось — не беда */
  }
}

export function toggle(hidden: Set<string>, id: string): Set<string> {
  if (!canHide(id)) return hidden;
  const next = new Set(hidden);
  if (next.has(id)) next.delete(id);
  else next.add(id);
  return next;
}

/** Вид для таблицы: `{ [id]: false }` у скрытых — так их ждёт TanStack Table. */
export function visibilityOf(hidden: Set<string>): Record<string, boolean> {
  const out: Record<string, boolean> = {};
  for (const id of hidden) if (canHide(id)) out[id] = false;
  return out;
}

/** Сколько скрыто — для подписи на кнопке. Ноль означает «показаны все». */
export function hiddenCount(hidden: Set<string>): number {
  return [...hidden].filter(canHide).length;
}

/**
 * ═══════════════════════════════════════════════════════════════════════
 * ПОРЯДОК ГРАФ — ТЗ 02.09.2026, раздел 2.1.
 *
 * «Порядок меняется перетаскиванием (HTML5 drag-and-drop), состояние —
 * clOrder; надписи групп пересчитываются от текущего порядка.»
 *
 * ХРАНИМ ИМЕНА, А НЕ НОМЕРА. Номер «третья графа» после выпуска, где
 * добавили «% корзин», указывает на другую графу — и сохранённый
 * порядок молча перемешает таблицу. Имя либо есть, либо его нет.
 *
 * СОХРАНЁННЫЙ ПОРЯДОК — ПОДСКАЗКА, А НЕ ЗАКОН. Он мог быть записан
 * старой версией, где граф было меньше. Поэтому имена, которых больше
 * нет, выбрасываются, а новые — те, которых человек не переставлял, —
 * встают туда, где их поставила таблица. Иначе новая графа уезжала бы
 * в конец у всех, кто хоть раз трогал порядок, и «её не завезли»
 * говорили бы про графу, которая есть.
 * ═══════════════════════════════════════════════════════════════════════
 */
export function mergeOrder(saved: readonly string[], все: readonly string[]): string[] {
  const известные = new Set(все);
  const переставленные = saved.filter((id) => известные.has(id));
  if (переставленные.length === 0) return [...все];
  const взяты = new Set(переставленные);

  /*
   * НОВАЯ ГРАФА ВСТАЁТ К СВОЕЙ СОСЕДКЕ СЛЕВА, а не в конец списка.
   * «% корзин» задумана рядом с «В корзину»; если человек однажды
   * переставил графы, она обязана появиться там же, где у всех
   * остальных, — иначе одно и то же обновление выглядит по-разному
   * у двух людей за соседними столами.
   */
  const итог = [...переставленные];
  for (let i = 0; i < все.length; i++) {
    const id = все[i];
    if (id === undefined || взяты.has(id)) continue;
    /* Ищем ближайшую слева графу, которая в порядке уже есть. */
    let куда = 0;
    for (let j = i - 1; j >= 0; j--) {
      const слева = все[j];
      if (слева === undefined) continue;
      const было = итог.indexOf(слева);
      if (было >= 0) {
        куда = было + 1;
        break;
      }
    }
    итог.splice(куда, 0, id);
    взяты.add(id);
  }
  return итог;
}

/**
 * Прочитать сохранённый порядок и свести его с нынешним набором граф.
 *
 * Порча памяти лечится так же, как у набора: возвращаем порядок
 * таблицы. Перемешанная таблица хуже, чем таблица без настройки.
 */
export function loadOrder(
  storage: Pick<Storage, 'getItem'>,
  все: readonly string[],
): string[] {
  try {
    const raw = storage.getItem(KEY_ORDER);
    if (!raw) return [...все];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [...все];
    return mergeOrder(
      parsed.filter((v): v is string => typeof v === 'string'),
      все,
    );
  } catch {
    return [...все];
  }
}

/** Сохранить порядок. Отказ хранилища таблицу не роняет — как и у набора. */
export function saveOrder(storage: Pick<Storage, 'setItem'>, order: readonly string[]): void {
  try {
    storage.setItem(KEY_ORDER, JSON.stringify([...order]));
  } catch {
    /* не сохранилось — не беда */
  }
}

/**
 * Переставить графу `id` НА МЕСТО графы `перед`.
 *
 * Не «поменять местами». Обмен местами — не то, что человек видит,
 * когда тащит строку через три другие: он ждёт, что она встанет туда,
 * куда он её принёс, а остальные сдвинутся. Обмен вместо этого
 * зашвыривает соседку через полсписка.
 */
export function moveBefore(order: readonly string[], id: string, перед: string): string[] {
  if (id === перед) return [...order];
  const из = order.indexOf(id);
  const в = order.indexOf(перед);
  if (из < 0 || в < 0) return [...order];
  const next = [...order];
  next.splice(из, 1);
  next.splice(next.indexOf(перед) + (из < в ? 1 : 0), 0, id);
  return next;
}

/**
 * «Вернуть как было» — ТЗ 02.09.2026: «сбрасывает набор И порядок».
 *
 * ОДНОЙ КНОПКОЙ ОБА, а не двумя разными. Человек, у которого таблица
 * стала непонятной, не разбирается, что он испортил — набор или
 * порядок; он хочет обратно ту, которую ему показали в первый раз.
 *
 * СТИРАЕМ ЗАПИСИ, А НЕ ПИШЕМ В НИХ ЭТАЛОН. Записанный эталон — это
 * «человек выбрал ровно это», и следующий выпуск, где набор по
 * умолчанию другой, обязан будет его уважать. Отсутствие записи
 * означает «не выбирал» — и эталон приедет сам.
 */
export function forgetAll(storage: Pick<Storage, 'removeItem'>): void {
  try {
    storage.removeItem(KEY);
    storage.removeItem(KEY_ORDER);
  } catch {
    /* не стёрлось — не беда */
  }
}
